import { useEffect, useState, useMemo, useRef } from "react";
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Header from "../components/Header";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import GoTo from "../components/GoTo";
import { Link } from "react-router-dom";
import { checkProductStock } from "../utils/stockApi";
import { toggleFavoriteProduct } from "../utils/wishlistApi";
import { useLanguage } from "../context/LanguageContext";
import { createSlug } from "../utils/slug";
import { getProductLink } from "../utils/productLink";
import { usePageMeta } from "../utils/usePageMeta";

function ProductDetailPage() {
  const { addItem, open } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { id: pathId } = useParams(); // Legacy support for /product/:id
  const location = useLocation();
  const stateProduct = location.state && location.state.product;
  const [searchParams] = useSearchParams();
  const productSlug = searchParams.get("product");
  const productIdFromQuery = searchParams.get("product_id"); // For immediate API fetching

  // Use product_id from query param, path param, or state product
  // Priority: product_id query param > path id > state product id
  const productId =
    productIdFromQuery ||
    pathId ||
    (stateProduct?.id ? String(stateProduct.id) : null);
  const [addError, setAddError] = useState("");
  const [liveStock, setLiveStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  // Responsive slider image sizing (React-driven)
  const [mainImgMaxH, setMainImgMaxH] = useState(540);
  const [thumbImgMaxH, setThumbImgMaxH] = useState(120);
  const [alternatives, setAlternatives] = useState([]);
  const [loadingAlternatives, setLoadingAlternatives] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [alternativesFavoriteState, setAlternativesFavoriteState] = useState(
    {},
  );

  // Quantity state - initialize with package quantity
  const [quantity, setQuantity] = useState(1);

  const {
    data: productData,
    isLoading: isLoadingProduct,
    isError: isErrorProduct,
    refetch: refetchProduct,
  } = useQuery({
    queryKey: ["productDetails", String(productId || ""), language],
    enabled: !!productId,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!productId) return null;

      let url = `https://erp-elsalab.odoo.com/api/cs/product/details?product_id=${encodeURIComponent(productId)}`;
      if (language === "ar") {
        url += "&lang=ar";
      }

      const res = await fetch(url, {
        headers: { Accept: "application/json; charset=utf-8" },
      });

      if (!res.ok) {
        throw new Error(`Failed to load product: ${res.status}`);
      }

      const buffer = await res.arrayBuffer();
      const text = new TextDecoder("utf-8").decode(buffer);
      const response = JSON.parse(text);

      // API returns {count, data} format - extract data
      return response?.data || null;
    },
    // Use stateProduct as initial data if available (for faster initial render)
    initialData: stateProduct || undefined,
    // Retry on error
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Use API data if available, fallback to stateProduct, then null
  const cachedProduct = productData || stateProduct || null;

  // Update URL with slug when product loads (for SEO)
  useEffect(() => {
    if (cachedProduct && cachedProduct.name && productId) {
      const slug = createSlug(cachedProduct.name);
      const currentPath = location.pathname;
      const currentSearch = location.search;

      // Check if we need to update the URL
      const needsUpdate =
        // If we have pathId (legacy /product/:id) and no slug in query
        (pathId && !productSlug) ||
        // If slug in query doesn't match current product slug
        (productSlug && productSlug !== slug);

      if (needsUpdate) {
        // Update URL to include slug in query param
        const newParams = new URLSearchParams(currentSearch);
        newParams.set("product", slug);
        newParams.set("product_id", productId);

        // Keep the path as /product/:id for backward compatibility, but add query params
        navigate(`${currentPath}?${newParams.toString()}`, { replace: true });
      }
    }
  }, [cachedProduct, productId, productSlug, pathId, location, navigate]);

  // Set page meta tags (title, description, etc.)
  // Use stateProduct first (immediate) for title, then fallback to cachedProduct (from API)
  // This ensures the title is set immediately when navigating from another page
  const productName = (stateProduct?.title || stateProduct?.name) || (cachedProduct?.title || cachedProduct?.name);
  const productDescription = (stateProduct?.description || cachedProduct?.description) || 
    (language === "ar"
      ? `${productName ? `${productName} - ` : ""}تسوق من عبدالعزيز السلاب`
      : `${productName ? `${productName} - ` : ""}Shop from Abdelaziz Elsallab`);
  
  // Canonical URL: The preferred/primary URL for this product page
  // This tells search engines which URL is the "official" one when there are multiple URLs
  // pointing to the same content (e.g., /product/123 vs /product/123?product=slug&product_id=123)
  // It helps prevent duplicate content issues and consolidates SEO ranking signals
  const canonicalUrl = productName && productId && typeof window !== "undefined"
    ? `${window.location.origin}${language === "ar" ? "/ar" : ""}/product/${productId}?product=${encodeURIComponent(createSlug(productName))}&product_id=${productId}`
    : undefined;

  usePageMeta({
    title: productName || undefined,
    description: productDescription,
    canonicalUrl: canonicalUrl,
  });

  const imageSrc =
    (cachedProduct &&
      (cachedProduct.image ||
        cachedProduct.image_url_1920 ||
        cachedProduct.image_url)) ||
    (productId
      ? `https://erp-elsalab.odoo.com/web/image/${productId}`
      : undefined);
  // Stock availability: always use API response when available, fallback to cached only if API failed
  const availableFree =
    liveStock !== null && liveStock !== undefined
      ? Number(liveStock)
      : Number(
          (cachedProduct &&
            cachedProduct.stock &&
            Array.isArray(cachedProduct.stock) &&
            cachedProduct.stock[0] &&
            cachedProduct.stock[0].total_free) ||
            0,
        );
  const isInStock = availableFree > 0;

  // Calculate package quantity after cachedProduct is available
  const packageQuantity =
    cachedProduct?.package_quantity || cachedProduct?.packageQuantity || 1;

  // Update quantity when product changes
  useEffect(() => {
    const newPackageQty =
      cachedProduct?.package_quantity || cachedProduct?.packageQuantity || 1;
    setQuantity(newPackageQty);
  }, [cachedProduct]);

  // Debug logging
  console.log("Stock calculation:", {
    liveStock,
    availableFree,
    isInStock,
    cachedStock: cachedProduct?.stock?.[0]?.total_free,
  });

  // Initialize favorite state from product data
  useEffect(() => {
    if (cachedProduct) {
      setIsFavorite(cachedProduct.is_favorite || false);
    }
  }, [cachedProduct]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check live stock when product loads (like mobile app)
  useEffect(() => {
    const fetchLiveStock = async () => {
      if (!productId || !cachedProduct) return;

      setStockLoading(true);
      try {
        const stock = await checkProductStock(Number(productId));
        console.log("Setting liveStock to:", stock);
        setLiveStock(stock);
      } catch (e) {
        console.warn("Failed to fetch live stock:", e.message);
        // Keep using cached stock data on error
      } finally {
        setStockLoading(false);
      }
    };

    fetchLiveStock();
  }, [productId, cachedProduct]);

  // React way: control image sizing based on viewport
  useEffect(() => {
    const applySizes = () => {
      const isMobile = window.innerWidth < 768;
      setMainImgMaxH(isMobile ? 320 : 540);
      setThumbImgMaxH(isMobile ? 80 : 120);
    };
    applySizes();
    window.addEventListener("resize", applySizes);
    return () => window.removeEventListener("resize", applySizes);
  }, []);

  // Initialize "You may also like" swiper after alternatives render (React way)
  useEffect(() => {
    if (!alternatives || alternatives.length === 0) return;
    if (!window || !window.Swiper) return;
    const root = document.querySelector(".you-may-like-swiper");
    if (!root || root.dataset.initialized === "true") return;
    try {
      const preview = Number(root.dataset.preview || 4);
      const tablet = Number(root.dataset.tablet || 3);
      const mobile = Number(root.dataset.mobile || 2);
      const spaceLg = Number(root.dataset.spaceLg ?? 30);
      const spaceMd = Number(root.dataset.spaceMd ?? 15);
      const space = Number(root.dataset.space ?? 15);
      const perGroup = Number(root.dataset.pagination || 1);
      const perGroupMd = Number(root.dataset.paginationMd || perGroup);
      const perGroupLg = Number(root.dataset.paginationLg || perGroup);

      // match Recently Viewed behavior
      // slidesPerView starts mobile, then increases at breakpoints
      const instance = new window.Swiper(".you-may-like-swiper", {
        slidesPerView: mobile,
        spaceBetween: space, // use main spacing like recent
        speed: 1000,
        pagination: { el: ".sw-pagination-youmay", clickable: true },
        navigation: {
          clickable: true,
          nextEl: ".nav-next-youmay",
          prevEl: ".nav-prev-youmay",
        },
        slidesPerGroup: perGroup,
        breakpoints: {
          768: {
            slidesPerView: tablet,
            spaceBetween: spaceLg,
            slidesPerGroup: perGroupMd,
          },
          1150: {
            slidesPerView: preview,
            spaceBetween: spaceLg,
            slidesPerGroup: perGroupLg,
          },
        },
      });
      if (instance) root.dataset.initialized = "true";
    } catch (e) {
      // no-op
    }
  }, [alternatives]);

  // Fetch product alternatives for "You may also like"
  useEffect(() => {
    const pid = Number(productId);
    if (!pid) return;
    let cancelled = false;
    const fetchAlternatives = async () => {
      try {
        setLoadingAlternatives(true);
        const res = await fetch(
          `https://erp-elsalab.odoo.com/api/cs/product-alternatives?product_id=${encodeURIComponent(
            pid,
          )}`,
          { headers: { Accept: "application/json; charset=utf-8" } },
        );
        if (!res.ok) throw new Error("Failed to load product alternatives");
        const buffer = await res.arrayBuffer();
        const text = new TextDecoder("utf-8").decode(buffer);
        const data = JSON.parse(text);
        const items = Array.isArray(data?.data) ? data.data : [];
        const mapped = items.map((p) => ({
          id: String(p.id || p.template_id || ""),
          title: p.name || "",
          name: p.name || "",
          price: typeof p.price === "number" ? p.price : null,
          image:
            p.image_url_1920 ||
            p.image_url ||
            (p.id ? `https://erp-elsalab.odoo.com/web/image/${p.id}` : ""),
          is_favorite: p.is_favorite || false,
        }));
        if (!cancelled) setAlternatives(mapped);
      } catch (e) {
        if (!cancelled) setAlternatives([]);
      } finally {
        if (!cancelled) setLoadingAlternatives(false);
      }
    };
    fetchAlternatives();
    return () => {
      cancelled = true;
    };
  }, [productId]);
  useEffect(() => {
    // Helpers
    const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
    const $ = (sel, ctx = document) => ctx.querySelector(sel);

    const on = (el, ev, fn) => {
      el.addEventListener(ev, fn);
      return () => el.removeEventListener(ev, fn);
    };
    const cleanups = [];

    // ===== 1) سلايدر الصور (Thumbs + Main) =====
    let thumbsSwiper, mainSwiper;
    const thumbsEl = $(".tf-product-media-thumbs");
    const mainEl = $(".tf-product-media-main");

    if (thumbsEl && mainEl && window.Swiper) {
      const direction = thumbsEl.dataset.direction || "vertical";

      thumbsSwiper = new window.Swiper(".tf-product-media-thumbs", {
        spaceBetween: 10,
        slidesPerView: "auto",
        freeMode: true,
        direction: "vertical",
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        breakpoints: {
          0: { direction: "horizontal", slidesPerView: 5 },
          1150: { direction },
        },
      });

      mainSwiper = new window.Swiper(".tf-product-media-main", {
        spaceBetween: 0,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: ".thumbs-next",
          prevEl: ".thumbs-prev",
        },
        thumbs: { swiper: thumbsSwiper },
      });

      // Ensure only two slides and both use the product image URL
      try {
        const slidesAll = $$(".tf-product-media-main .swiper-slide");
        if (slidesAll && slidesAll.length > 0) {
          // Set second slide to use same image
          if (slidesAll[1]) {
            const imgs = slidesAll[1].querySelectorAll("img");
            imgs.forEach((img) => {
              if (typeof imageSrc === "string" && imageSrc) {
                img.src = imageSrc;
                img.setAttribute("data-src", imageSrc);
                img.setAttribute("data-zoom", imageSrc);
              }
            });
            const a = slidesAll[1].querySelector("a.item");
            if (a && typeof imageSrc === "string" && imageSrc) {
              a.href = imageSrc;
            }
          }
          // Remove any slides after the first two
          if (slidesAll.length > 2) {
            slidesAll.slice(2).forEach((el) => el.remove());
          }
          mainSwiper.update && mainSwiper.update();
        }
      } catch (e) {
        // no-op safeguard
      }

      // تحديث زرار اللون + اللّيبلز
      const updateActiveColorButton = (activeIndex) => {
        $$(".color-btn").forEach((b) => b.classList.remove("active"));
        const currentSlide = $$(".tf-product-media-main .swiper-slide")[
          activeIndex
        ];
        if (!currentSlide) return;
        const currentColor = currentSlide.getAttribute("data-color");
        if (currentColor) {
          const btn = $(`.color-btn[data-color="${currentColor}"]`);
          btn && btn.classList.add("active");
          const out1 = $(".value-currentColor");
          const out2 = $(".select-currentColor");
          if (out1) out1.textContent = currentColor;
          if (out2) out2.textContent = currentColor;
        }
      };

      mainSwiper.on("slideChange", function () {
        updateActiveColorButton(this.activeIndex);
      });

      const scrollToColor = (color) => {
        const slides = $$(".tf-product-media-main .swiper-slide");
        const idx = slides.findIndex(
          (s) => s.getAttribute("data-color") === color,
        );
        if (idx >= 0) {
          mainSwiper.slideTo(idx, 300, false);
          thumbsSwiper.slideTo(idx, 300, false);
        }
      };

      // ربط أزرار الألوان
      const colorBtns = $$(".color-btn");
      colorBtns.forEach((btn) => {
        const off = on(btn, "click", () => {
          colorBtns.forEach((b) => b.classList.remove("active"));
          btn.classList.add("active");
          scrollToColor(btn.getAttribute("data-color"));
        });
        cleanups.push(off);
      });

      updateActiveColorButton(mainSwiper.activeIndex);
      window.__pdMainSwiper = mainSwiper; // علشان PhotoSwipe
    }

    // ===== 2) Drift Zoom (Mouse zoom) =====
    const zoomImgs = $$(".tf-image-zoom");
    const zoomPane = $(".tf-zoom-main");
    const driftInstances = [];
    if (
      zoomImgs.length &&
      zoomPane &&
      window.matchMedia("(min-width: 768px)").matches &&
      window.Drift
    ) {
      zoomImgs.forEach((el) => {
        const d = new window.Drift(el, {
          zoomFactor: 2,
          paneContainer: zoomPane,
          inlinePane: false,
          handleTouch: false,
          hoverBoundingBox: true,
          containInline: true,
        });
        driftInstances.push(d);

        // نفس سلوك الثيم (إظهار/إخفاء حالة الزوم على السكشن)
        const over = on(el, "mouseover", () =>
          el.closest(".section-image-zoom")?.classList.add("zoom-active"),
        );
        const leave = on(el, "mouseleave", () =>
          el.closest(".section-image-zoom")?.classList.remove("zoom-active"),
        );
        cleanups.push(over, leave);
      });
    }

    // ===== 3) PhotoSwipe Lightbox مربوط بالسلايدر =====
    let lightbox;
    if (
      mainEl &&
      $("#gallery-swiper-started") &&
      window.PhotoSwipeLightbox &&
      window.PhotoSwipe
    ) {
      lightbox = new window.PhotoSwipeLightbox({
        gallery: "#gallery-swiper-started",
        children: "a",
        pswpModule: window.PhotoSwipe,
        bgOpacity: 1,
        secondaryZoomLevel: 2,
        maxZoomLevel: 3,
      });
      lightbox.init();

      lightbox.on("change", () => {
        const pswp = lightbox.pswp;
        if (window.__pdMainSwiper && pswp)
          window.__pdMainSwiper.slideTo(pswp.currIndex, 0, false);
      });

      lightbox.on("afterInit", () => {
        if (window.__pdMainSwiper?.params?.autoplay?.enabled)
          window.__pdMainSwiper.autoplay.stop();
      });

      lightbox.on("closingAnimationStart", () => {
        const pswp = lightbox.pswp;
        if (window.__pdMainSwiper && pswp)
          window.__pdMainSwiper.slideTo(pswp.currIndex, 0, false);
        if (window.__pdMainSwiper?.params?.autoplay?.enabled)
          window.__pdMainSwiper.autoplay.start();
      });
    }

    // ===== 4) سلايدرات "People Also Bought" و "Recently Viewed" =====
    const swipersToDestroy = [];
    const initSwiperFromAttrs = (selector, opts) => {
      const root = $(selector);
      if (!root || !window.Swiper) return;

      const preview = Number(root.dataset.preview || 4);
      const tablet = Number(root.dataset.tablet || 3);
      const mobile = Number(root.dataset.mobile || 2);
      const spaceLg = Number(root.dataset.spaceLg ?? 30);
      const spaceMd = Number(root.dataset.spaceMd ?? 15);
      const space = Number(root.dataset.space ?? 15);
      const perGroup = Number(root.dataset.pagination || 1);
      const perGroupMd = Number(root.dataset.paginationMd || perGroup);
      const perGroupLg = Number(root.dataset.paginationLg || perGroup);

      const instance = new window.Swiper(selector, {
        slidesPerView: mobile,
        spaceBetween: opts.useSpace ? space : spaceMd,
        speed: 1000,
        pagination: opts.pagination
          ? { el: opts.pagination, clickable: true }
          : undefined,
        navigation: opts.navigation
          ? {
              clickable: true,
              nextEl: opts.navigation.nextEl,
              prevEl: opts.navigation.prevEl,
            }
          : undefined,
        slidesPerGroup: perGroup,
        breakpoints: {
          768: {
            slidesPerView: tablet,
            spaceBetween: spaceLg,
            slidesPerGroup: perGroupMd,
          },
          1150: {
            slidesPerView: preview,
            spaceBetween: spaceLg,
            slidesPerGroup: perGroupLg,
          },
        },
      });

      swipersToDestroy.push(instance);
    };

    initSwiperFromAttrs(".tf-sw-product-sell", {
      pagination: ".sw-pagination-product",
      navigation: { nextEl: ".nav-next-product", prevEl: ".nav-prev-product" },
      useSpace: false,
    });
    initSwiperFromAttrs(".tf-sw-recent:not([data-disabled='true'])", {
      pagination: ".sw-pagination-recent",
      navigation: { nextEl: ".nav-next-recent", prevEl: ".nav-prev-recent" },
      useSpace: true,
    });
    initSwiperFromAttrs(".tf-sw-recent", {
      pagination: ".sw-pagination-recent",
      navigation: { nextEl: ".nav-next-recent", prevEl: ".nav-prev-recent" },
      useSpace: true,
    });

    // ===== 5) الألسنة (Tabs) — بديل جزء tabs من main.js =====
    $$(".widget-tabs").forEach((wrap) => {
      const titles = $$(".widget-menu-tab .item-title", wrap);
      const panes = $$(".widget-content-tab .widget-content-inner", wrap);
      titles.forEach((title, idx) => {
        const off = on(title, "click", () => {
          titles.forEach((t) => t.classList.remove("active"));
          panes.forEach((p) => p.classList.remove("active"));
          title.classList.add("active");
          panes[idx]?.classList.add("active");
        });
        cleanups.push(off);
      });
    });

    // ===== 6) Quantity buttons are now handled by React state =====
    // Removed vanilla JS handlers - quantity is now managed via React state

    // ===== 7) Dropdown sort (في التبويب Reviews) =====
    $$(".tf-dropdown-sort").forEach((drop) => {
      const valueEl = $(".text-sort-value", drop);
      $$(".dropdown-menu .select-item", drop).forEach((item) => {
        const off = on(item, "click", () => {
          $$(".dropdown-menu .select-item", drop).forEach((i) =>
            i.classList.remove("active"),
          );
          item.classList.add("active");
          const text = $(".text-value-item", item)?.textContent?.trim() || "";
          if (valueEl && text) valueEl.textContent = text;
        });
        cleanups.push(off);
      });
    });

    // ===== 8) Color swatch داخل كروت المنتجات (تبديل صورة المعاينة) =====
    $$(".card-product").forEach((card) => {
      const img = $(".img-product", card);
      if (!img) return;
      $$(".list-color-product .color-swatch", card).forEach((sw) => {
        const hover = on(sw, "mouseenter", () => {
          const hiddenImg = $("img.lazyload", sw) || $("img", sw);
          if (hiddenImg?.src) img.src = hiddenImg.src;
          $$(".list-color-product .color-swatch", card).forEach((s) =>
            s.classList.remove("active"),
          );
          sw.classList.add("active");
        });
        const click = on(sw, "click", (e) => {
          e.preventDefault();
          sw.dispatchEvent(new Event("mouseenter"));
        });
        cleanups.push(hover, click);
      });
    });

    // ========== Cleanup ==========
    return () => {
      try {
        lightbox && lightbox.destroy?.();
      } catch {}
      try {
        driftInstances.forEach((d) => d.disable?.());
      } catch {}
      try {
        mainSwiper && mainSwiper.destroy?.();
      } catch {}
      try {
        thumbsSwiper && thumbsSwiper.destroy?.();
      } catch {}
      try {
        swipersToDestroy.forEach((sw) => sw.destroy?.());
      } catch {}
      cleanups.forEach((off) => {
        try {
          off();
        } catch {}
      });
    };
  }, []);
  return (
    <div>
      <Header />

      {/* breadcrumb */}
      <div className="tf-breadcrumb">
        <div className="container">
          <div className="tf-breadcrumb-wrap d-flex justify-content-between flex-wrap align-items-center">
            <div className="tf-breadcrumb-list">
              <Link to="/" className="text">
                {language === "ar" ? "الرئيسية" : "Home"}
              </Link>
              <i className="icon icon-arrow-right"></i>
              <Link to="/" className="text">
                {cachedProduct?.categ_name ||
                  cachedProduct?.current_offer ||
                  (language === "ar" ? "الفئة" : "Category")}
              </Link>
              <i className="icon icon-arrow-right"></i>
              <span className="text">
                {cachedProduct?.title ||
                  cachedProduct?.name ||
                  (language === "ar" ? "المنتج" : "product")}
              </span>
            </div>
            {/* Removed top-right breadcrumb navigation (prev/back/next + grid icon) */}
          </div>
        </div>
      </div>
      {/* /breadcrumb */}

      {/* Loading state */}
      {isLoadingProduct && !cachedProduct && (
        <section className="flat-spacing-4 pt_0 product-section">
          <div className="container">
            <div className="preload preload-container w-100 text-center py-5">
              <div className="preload-logo">
                <div className="spinner"></div>
              </div>
              <p style={{ marginTop: "20px" }}>
                {language === "ar" ? "جاري التحميل..." : "Loading product..."}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Error state */}
      {isErrorProduct && !cachedProduct && (
        <section className="flat-spacing-4 pt_0 product-section">
          <div className="container">
            <div className="text-center py-5">
              <p style={{ color: "red", marginBottom: "20px" }}>
                {language === "ar"
                  ? "فشل تحميل المنتج"
                  : "Failed to load product"}
              </p>
              <button
                onClick={() => refetchProduct()}
                className="tf-btn btn-fill"
              >
                {language === "ar" ? "إعادة المحاولة" : "Retry"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Product content - show if not loading or if we have cached data */}
      {(!isLoadingProduct || cachedProduct) && (
        <>
          <section className="flat-spacing-4 pt_0 product-section">
            <style>
              {`
            /* Reduce vertical spacing in product detail page */
            .tf-product-info-list > * {
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-title {
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-badges {
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-price {
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-quantity {
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-quantity .wg-quantity {
              height: 36px !important;
            }
            
            .tf-product-info-quantity .btn-quantity {
              height: 36px !important;
              min-width: 36px !important;
              line-height: 36px !important;
              font-size: 14px !important;
            }
            
            .tf-product-info-quantity .quantity-product {
              height: 36px !important;
              line-height: 36px !important;
              padding: 0 8px !important;
              font-size: 14px !important;
            }
            
            /* Sticky quantity editor - same smaller height */
            .tf-sticky-atc-btns .tf-product-info-quantity .wg-quantity {
              height: 36px !important;
            }
            
            .tf-sticky-atc-btns .tf-product-info-quantity .btn-quantity {
              height: 36px !important;
              min-width: 36px !important;
              line-height: 36px !important;
              font-size: 14px !important;
            }
            
            .tf-sticky-atc-btns .tf-product-info-quantity input[type="text"] {
              height: 36px !important;
              line-height: 36px !important;
              padding: 0 8px !important;
              font-size: 14px !important;
            }
            
            .tf-product-info-buy-button {
              margin-bottom: 12px !important;
            }
            
            /* Align buttons in buy button section */
            .tf-product-info-buy-button form {
              display: flex !important;
              align-items: center !important;
              gap: 12px !important;
            }
            
            .tf-product-info-buy-button .btn-add-to-cart {
              flex: 1 1 auto !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              height: 48px !important;
              min-height: 48px !important;
              line-height: 1 !important;
              padding: 0 !important;
            }
            
            /* Make wishlist icon smaller and bluish, aligned with add to cart button */
            .tf-product-btn-wishlist,
            .tf-product-btn-wishlist.box-icon {
              width: 48px !important;
              height: 48px !important;
              min-width: 48px !important;
              min-height: 48px !important;
              padding: 0 !important;
              margin: 0 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              flex-shrink: 0 !important;
              vertical-align: middle !important;
              line-height: 1 !important;
            }
            
            /* Remove any margin or padding that might cause misalignment */
            .tf-product-info-buy-button form > * {
              margin: 0 !important;
              vertical-align: middle !important;
            }
            
            .tf-product-btn-wishlist .icon,
            .tf-product-btn-wishlist .icon:before,
            .tf-product-btn-wishlist .icon-heart {
              font-size: 16px !important;
              color: #0e5aa7 !important;
            }
            
            .tf-product-btn-wishlist:hover .icon,
            .tf-product-btn-wishlist:hover .icon:before,
            .tf-product-btn-wishlist:hover .icon-heart {
              color: #0e5aa7 !important;
            }
            
            .tf-product-btn-wishlist.active .icon,
            .tf-product-btn-wishlist.active .icon:before,
            .tf-product-btn-wishlist.active .icon-heart {
              color: #0e5aa7 !important;
            }
            
            /* Wishlist in alternatives section - smaller and bluish */
            .you-may-like-swiper .wishlist,
            .you-may-like-swiper .wishlist.box-icon {
              width: 36px !important;
              height: 36px !important;
              min-width: 36px !important;
              padding: 0 !important;
            }
            
            .you-may-like-swiper .wishlist .icon,
            .you-may-like-swiper .wishlist .icon:before,
            .you-may-like-swiper .wishlist .icon-heart {
              font-size: 14px !important;
              color: #0e5aa7 !important;
            }
            
            .you-may-like-swiper .wishlist:hover .icon,
            .you-may-like-swiper .wishlist:hover .icon:before,
            .you-may-like-swiper .wishlist:hover .icon-heart {
              color: #0e5aa7 !important;
            }
            
            .you-may-like-swiper .wishlist.active .icon,
            .you-may-like-swiper .wishlist.active .icon:before,
            .you-may-like-swiper .wishlist.active .icon-heart {
              color: #0e5aa7 !important;
            }
            
            .tf-product-info-delivery-return {
              margin-top: 12px !important;
              margin-bottom: 12px !important;
            }
            
            .tf-product-info-trust-seal {
              margin-top: 12px !important;
              margin-bottom: 0 !important;
            }
            
            .mb-2.text_black-2 {
              margin-bottom: 12px !important;
            }
            
            /* Ensure product image fills entire left column on desktop */
            @media (min-width: 768px) {
              .product-section {
                padding-top: 0 !important;
                margin-top: 0 !important;
              }
              
              .product-section .container {
                padding-top: 0 !important;
              }
              
              .product-section .row {
                margin-top: 0 !important;
                padding-top: 0 !important;
                align-items: flex-start !important;
              }
              
              .product-section .col-md-6:first-child {
                display: block !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-main-product {
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-product-media-wrap {
                width: 100% !important;
                height: auto !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
                position: relative !important;
                top: 0 !important;
              }
              
              .tf-product-media-wrap.sticky-top {
                position: relative !important;
                top: 0 !important;
              }
              
              .tf-product-media-wrap .thumbs-slider {
                width: 100% !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
              }
              
              /* Hide thumbs slider on desktop to save space */
              .tf-product-media-thumbs {
                display: none !important;
              }
              
              .tf-product-media-main {
                width: 100% !important;
                max-width: 100% !important;
                min-height: 400px !important;
                height: auto !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-product-media-main .swiper-wrapper {
                height: auto !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-product-media-main .swiper-slide {
                height: auto !important;
                min-height: 400px !important;
                display: flex !important;
                align-items: flex-start !important;
                justify-content: center !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-product-media-main .swiper-slide .item {
                width: 100% !important;
                max-width: 90% !important;
                height: auto !important;
                display: flex !important;
                align-items: flex-start !important;
                justify-content: center !important;
                margin: 0 auto !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
              
              .tf-product-media-main .swiper-slide img {
                width: 100% !important;
                max-width: 90% !important;
                height: auto !important;
                object-fit: contain !important;
                margin-top: 0 !important;
                padding-top: 0 !important;
              }
            }
            
            /* Reduce spacing below image on mobile */
            @media (max-width: 767px) {
              .tf-product-media-wrap {
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
              }
              
              .tf-product-media-main {
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
              }
              
              .tf-product-media-wrap .thumbs-slider {
                margin-bottom: 10px !important;
                padding-bottom: 0 !important;
              }
              
              /* Hide thumbs slider on mobile */
              .tf-product-media-thumbs {
                display: none !important;
              }
              
              /* Hide swiper navigation buttons */
              .tf-product-media-main .swiper-button-next,
              .tf-product-media-main .swiper-button-prev {
                display: none !important;
              }
              
              /* Hide all slides except the first one */
              .tf-product-media-main .swiper-slide:not(:first-child) {
                display: none !important;
              }
              
              /* Make first slide take full width */
              .tf-product-media-main .swiper-slide:first-child .item {
                width: 100% !important;
                overflow: hidden !important;
              }
              
              .tf-product-media-main .swiper-slide:first-child .item a {
                width: 100% !important;
                display: block !important;
              }
              
              .tf-product-media-main .swiper-slide:first-child img {
                width: 100% !important;
                height: auto !important;
                object-fit: contain !important;
                display: block !important;
              }
              
              .product-section .row {
                margin-bottom: 0 !important;
              }
              
              .product-section .col-md-6:first-child {
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
                position: relative !important;
                z-index: 1 !important;
              }
              
              .product-section .col-md-6:last-child {
                margin-top: 0 !important;
                padding-top: 0 !important;
                position: relative !important;
                z-index: 2 !important;
              }
              
              .tf-product-info-wrap {
                margin-top: 0 !important;
                padding-top: 0 !important;
                position: relative !important;
                z-index: 2 !important;
              }
              
              .tf-product-info-title {
                position: relative !important;
                z-index: 3 !important;
                background: #ffffff !important;
                padding: 10px 0 !important;
                margin-top: 0 !important;
                margin-bottom: 0 !important;
              }
              
              .product-section {
                padding-bottom: 0 !important;
                margin-bottom: 0 !important;
              }
              
              .product-section .container {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
              
              .tf-main-product {
                margin-bottom: 0 !important;
                padding-bottom: 0 !important;
              }
            }
          `}
            </style>
            <div className="tf-main-product section-image-zoom">
              <div className="container">
                <div className="row">
                  <div className="col-md-6">
                    <div className="tf-product-media-wrap sticky-top">
                      <div className="thumbs-slider">
                        <div
                          dir="ltr"
                          className="swiper tf-product-media-thumbs other-image-zoom"
                          data-direction="vertical"
                        >
                          <div className="swiper-wrapper stagger-wrap">
                            {/* beige */}
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="beige"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            {/* black */}
                            <div
                              className="swiper-slide stagger-item"
                              data-color="black"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="black"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="black"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="black"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            {/* blue */}
                            <div
                              className="swiper-slide stagger-item"
                              data-color="blue"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="blue"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="blue"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="blue"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            {/* white */}
                            <div
                              className="swiper-slide stagger-item"
                              data-color="white"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="white"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="white"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                            <div
                              className="swiper-slide stagger-item"
                              data-color="white"
                            >
                              <div className="item">
                                <img
                                  className="lazyload"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          dir="ltr"
                          className="swiper tf-product-media-main"
                          id="gallery-swiper-started"
                        >
                          <div className="swiper-wrapper">
                            {/* primary image from product */}
                            <div className="swiper-slide" data-color="default">
                              <a
                                href={imageSrc}
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom={imageSrc}
                                  data-src={imageSrc}
                                  src={imageSrc}
                                  alt={
                                    cachedProduct?.title ||
                                    cachedProduct?.name ||
                                    "product"
                                  }
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="beige">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="beige">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="beige">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="beige">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="beige">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            {/* black */}
                            <div className="swiper-slide" data-color="black">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="black">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="black">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="black">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt=""
                                />
                              </a>
                            </div>
                            {/* blue */}
                            <div className="swiper-slide" data-color="blue">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="blue">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="blue">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="blue">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            {/* white */}
                            <div className="swiper-slide" data-color="white">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="white">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="white">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                            <div className="swiper-slide" data-color="white">
                              <a
                                href="https://erp-elsalab.odoo.com/web/image/530564"
                                rel="noreferrer"
                                className="item"
                                data-pswp-width="770"
                                data-pswp-height="1075"
                              >
                                <img
                                  className="tf-image-zoom lazyload"
                                  data-zoom="https://erp-elsalab.odoo.com/web/image/530564"
                                  data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                  src="https://erp-elsalab.odoo.com/web/image/530564"
                                  alt="img-product"
                                />
                              </a>
                            </div>
                          </div>
                          <div className="swiper-button-next button-style-arrow thumbs-next"></div>
                          <div className="swiper-button-prev button-style-arrow thumbs-prev"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right column */}
                  <div className="col-md-6">
                    <div className="tf-product-info-wrap position-relative">
                      <div className="tf-zoom-main"></div>
                      <div className="tf-product-info-list other-image-zoom">
                        <div className="tf-product-info-title">
                          <h5>
                            {cachedProduct?.title ||
                              cachedProduct?.name ||
                              "Product"}
                          </h5>
                        </div>

                        <div className="tf-product-info-price">
                          {cachedProduct?.price != null && (
                            <div className="price-on-sale">
                              EGP {Number(cachedProduct.price).toFixed(2)}
                            </div>
                          )}
                          {cachedProduct?.list_price != null && (
                            <div className="compare-at-price">
                              {Number(cachedProduct.list_price).toFixed(2)}
                            </div>
                          )}
                          {cachedProduct?.list_price &&
                            cachedProduct?.price && (
                              <div className="badges-on-sale">
                                <span>
                                  {Math.max(
                                    0,
                                    Math.round(
                                      ((cachedProduct.list_price -
                                        cachedProduct.price) /
                                        cachedProduct.list_price) *
                                        100,
                                    ),
                                  )}
                                </span>
                                % OFF
                              </div>
                            )}
                        </div>

                        <div className="tf-product-info-badges">
                          <div className="badges">
                            {language === "ar"
                              ? "الأكثر مبيعاً"
                              : "Best seller"}
                          </div>
                          <div className="product-status-content">
                            <i className="icon-lightning"></i>
                            <p className="fw-6">
                              {language === "ar"
                                ? "يُباع بسرعة! في العديد من السلات."
                                : "Selling fast! on many carts."}
                            </p>
                          </div>
                        </div>

                        {/* <div className="tf-product-info-liveview">
                      <div className="liveview-count">20</div>
                      <p className="fw-6">People are viewing this right now</p>
                    </div> */}

                        {/* <div className="tf-product-info-countdown">
                      <div className="countdown-wrap">
                        <div className="countdown-title">
                          <i className="icon-time tf-ani-tada"></i>
                          <p>HURRY UP! SALE ENDS IN:</p>
                        </div>
                        <div className="tf-countdown style-1">
                          <div
                            className="js-countdown"
                            data-timer="1007500"
                            data-labels="Days :,Hours :,Mins :,Secs"
                          ></div>
                        </div>
                      </div>
                    </div> */}

                        <div className="mb-2 text_black-2">
                          <strong>
                            {language === "ar" ? "الحالة:" : "Status:"}
                          </strong>{" "}
                          {stockLoading ? (
                            <span className="text-muted">
                              {language === "ar"
                                ? "جاري التحقق من المخزون..."
                                : "Checking stock..."}
                            </span>
                          ) : (
                            <>
                              {isInStock
                                ? language === "ar"
                                  ? "متوفر"
                                  : "In stock"
                                : language === "ar"
                                  ? "عند الطلب"
                                  : "On request"}
                              {isInStock &&
                              availableFree > 0 &&
                              availableFree <= 10
                                ? ` (${language === "ar" ? "متوفر:" : "Available:"} ${availableFree})`
                                : ""}
                            </>
                          )}
                        </div>

                        <div className="mb-2 text_black-2">
                          <strong>
                            {language === "ar"
                              ? "كمية العبوة:"
                              : "Package Quantity:"}
                          </strong>{" "}
                          {cachedProduct?.package_quantity ||
                            cachedProduct?.packageQuantity ||
                            1}
                        </div>

                        <div className="tf-product-info-quantity">
                          <div className="quantity-title fw-6">
                            {language === "ar" ? "الكمية" : "Quantity"}
                          </div>
                          <div className="wg-quantity">
                            <span
                              className="btn-quantity btn-decrease"
                              onClick={() => {
                                const newQty = Math.max(
                                  packageQuantity,
                                  quantity - packageQuantity,
                                );
                                setQuantity(newQty);
                              }}
                            >
                              -
                            </span>
                            <input
                              type="text"
                              className="quantity-product"
                              name="number"
                              value={quantity}
                              onChange={(e) => {
                                const value =
                                  parseFloat(e.target.value) || packageQuantity;
                                setQuantity(Math.max(packageQuantity, value));
                              }}
                              data-package-qty={packageQuantity}
                            />
                            <span
                              className="btn-quantity btn-increase"
                              onClick={() => {
                                setQuantity(quantity + packageQuantity);
                              }}
                            >
                              +
                            </span>
                          </div>
                        </div>

                        <div className="tf-product-info-buy-button">
                          <form className="">
                            {addError && (
                              <div
                                className="alert alert-danger mb-3"
                                role="alert"
                              >
                                {addError}
                              </div>
                            )}
                            <a
                              href="#"
                              className="tf-btn btn-fill justify-content-center fw-6 fs-16 flex-grow-1 animate-hover-btn btn-add-to-cart"
                              onClick={(e) => {
                                e.preventDefault();
                                setAddError("");
                                if (cachedProduct) {
                                  // Use the quantity state directly - ensure it's at least packageQuantity
                                  const qty = Math.max(
                                    packageQuantity,
                                    quantity,
                                  );
                                  console.log(
                                    "Add to cart - quantity state:",
                                    quantity,
                                    "packageQuantity:",
                                    packageQuantity,
                                    "final qty:",
                                    qty,
                                  );
                                  // Enforce stock availability similar to mobile app
                                  if (!isInStock || availableFree <= 0) {
                                    setAddError(
                                      language === "ar"
                                        ? "هذا المنتج غير متوفر حالياً. يرجى الاتصال بالدعم أو التحقق لاحقاً."
                                        : "This item is currently on request. Please contact support or check later.",
                                    );
                                    return;
                                  }
                                  if (qty > availableFree) {
                                    setAddError(
                                      language === "ar"
                                        ? `متوفر فقط ${availableFree} وحدة في المخزون.`
                                        : `Only ${availableFree} units available in stock.`,
                                    );
                                    return;
                                  }
                                  console.log("Calling addItem with qty:", qty);
                                  addItem({
                                    ...cachedProduct,
                                    id: productId || cachedProduct.id,
                                    qty: qty, // Pass the calculated quantity
                                  });
                                  open();
                                }
                              }}
                            >
                              <span>
                                {language === "ar"
                                  ? "أضف إلى السلة"
                                  : "Add to cart"}
                                &nbsp;
                              </span>
                              {/* <span className="tf-qty-price total-price">
                            EGP {Number(cachedProduct?.price || 0).toFixed(2)}
                          </span> */}
                            </a>
                            <button
                              type="button"
                              className={`tf-product-btn-wishlist hover-tooltip box-icon bg_white wishlist btn-icon-action ${
                                isFavorite ? "active" : ""
                              }`}
                              onClick={async (e) => {
                                e.preventDefault();
                                if (!cachedProduct || !productId) return;
                                try {
                                  const newFavoriteState = !isFavorite;

                                  // Optimistically update local state
                                  setIsFavorite(newFavoriteState);

                                  await toggleFavoriteProduct(
                                    parseInt(productId),
                                    newFavoriteState,
                                    null,
                                    cachedProduct,
                                  );

                                  console.log(
                                    `Product ${productId} ${
                                      newFavoriteState
                                        ? "added to"
                                        : "removed from"
                                    } wishlist`,
                                  );
                                } catch (error) {
                                  // Revert on error
                                  setIsFavorite(
                                    cachedProduct.is_favorite || false,
                                  );
                                  console.error(
                                    "Failed to toggle favorite:",
                                    error,
                                  );
                                  alert(
                                    error.message ||
                                      (language === "ar"
                                        ? "فشل تحديث قائمة الأمنيات"
                                        : "Failed to update wishlist"),
                                  );
                                }
                              }}
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">
                                {isFavorite
                                  ? language === "ar"
                                    ? "إزالة من قائمة الأمنيات"
                                    : "Remove from Wishlist"
                                  : language === "ar"
                                    ? "أضف إلى قائمة الأمنيات"
                                    : "Add to Wishlist"}
                              </span>
                              <span className="icon icon-delete"></span>
                            </button>
                            {/* <div className="w-100">
                          <a href="#" className="btns-full">
                            Buy with{" "}
                            <img src="images/payments/paypal.png" alt="" />
                          </a>
                          <a href="#" className="payment-more-option">
                            More payment options
                          </a>
                        </div> */}
                          </form>
                        </div>

                        {/* Extra links removed per request */}

                        <div className="tf-product-info-delivery-return">
                          <div className="row">
                            <div className="col-xl-6 col-12">
                              <div className="tf-product-delivery">
                                <div className="icon">
                                  <i className="icon-delivery-time"></i>
                                </div>
                                <p>
                                  {language === "ar"
                                    ? "وقت التسليم المتوقع:"
                                    : "Estimate delivery times:"}{" "}
                                  <span className="fw-7">
                                    5-10 {language === "ar" ? "أيام" : "days"}
                                  </span>{" "}
                                </p>
                              </div>
                            </div>
                            <div className="col-xl-6 col-12">
                              <div className="tf-product-delivery mb-0">
                                <div className="icon">
                                  <i className="icon-return-order"></i>
                                </div>
                                <p>
                                  {language === "ar"
                                    ? "الإرجاع خلال"
                                    : "Return within"}{" "}
                                  <span className="fw-7">
                                    30 {language === "ar" ? "يوماً" : "days"}
                                  </span>{" "}
                                  {language === "ar"
                                    ? "من الشراء."
                                    : "of purchase."}{" "}
                                  {language === "ar"
                                    ? "الرسوم والضرائب غير قابلة للاسترداد."
                                    : "Duties & taxes are non-refundable."}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="tf-product-info-trust-seal">
                          <div className="tf-product-trust-mess">
                            <i className="icon-safe"></i>
                            <p className="fw-6">
                              {language === "ar"
                                ? "ضمان الدفع الآمن"
                                : "Guarantee Safe"}{" "}
                              <br /> {language === "ar" ? "" : "Checkout"}
                            </p>
                          </div>
                          {/* <div className="tf-payment">
                        <img src="images/payments/visa.png" alt="" />
                        <img src="images/payments/img-1.png" alt="" />
                        <img src="images/payments/img-2.png" alt="" />
                        <img src="images/payments/img-3.png" alt="" />
                        <img src="images/payments/img-4.png" alt="" />
                      </div> */}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Right column */}
                </div>
              </div>
            </div>

            <div className="tf-sticky-btn-atc">
              <style>
                {`
              @media (max-width: 767px) {
                /* Fix overflow and make compact on mobile */
                .tf-sticky-btn-atc {
                  width: 100% !important;
                  max-width: 100vw !important;
                  overflow: hidden !important;
                }

                .tf-sticky-btn-atc .container {
                  padding-left: 8px !important;
                  padding-right: 8px !important;
                  max-width: 100% !important;
                }

                .tf-sticky-btn-atc .tf-height-observer {
                  gap: 8px !important;
                  flex-wrap: nowrap !important;
                }

                /* Hide product image on mobile */
                .tf-sticky-btn-atc .tf-sticky-atc-img {
                  display: none !important;
                }

                /* Hide dropdown on mobile */
                .tf-sticky-btn-atc .tf-sticky-atc-variant-price {
                  display: none !important;
                }

                /* Make buttons more compact */
                .tf-sticky-btn-atc .tf-sticky-atc-btns {
                  display: flex !important;
                  gap: 8px !important;
                  width: 100% !important;
                  flex: 1 1 auto !important;
                }

                /* Hide quantity adder on mobile */
                .tf-sticky-btn-atc .tf-product-info-quantity {
                  display: none !important;
                }

                .tf-sticky-btn-atc .btn-add-to-cart {
                  min-width: 0 !important;
                  flex: 1 1 auto !important;
                  white-space: nowrap !important;
                }
              }
            `}
              </style>
              <div className="container">
                <div className="tf-height-observer w-100 d-flex align-items-center">
                  <div className="tf-sticky-atc-product d-flex align-items-center d-none d-md-flex">
                    <div className="tf-sticky-atc-img">
                      <img
                        className="lazyloaded"
                        data-src={imageSrc}
                        alt={
                          cachedProduct?.title ||
                          cachedProduct?.name ||
                          "product"
                        }
                        src={imageSrc}
                      />
                    </div>
                    <div className="tf-sticky-atc-title fw-5 d-xl-block d-none">
                      {cachedProduct?.title || cachedProduct?.name || ""}
                    </div>
                  </div>
                  <div className="tf-sticky-atc-infos">
                    <form className="">
                      <div className="tf-sticky-atc-variant-price text-center d-none d-md-block">
                        <select className="tf-select" disabled>
                          <option>
                            {cachedProduct?.att_color || "-"}
                            {" / "}
                            {cachedProduct?.att_size || "-"}
                            {cachedProduct?.price != null
                              ? ` - EGP ${Number(cachedProduct.price).toFixed(2)}`
                              : ""}
                          </option>
                        </select>
                      </div>
                      <div className="tf-sticky-atc-btns">
                        <div className="tf-product-info-quantity d-none d-md-block">
                          <div className="wg-quantity">
                            <span
                              className="btn-quantity minus-btn"
                              onClick={() => {
                                const newQty = Math.max(
                                  packageQuantity,
                                  quantity - packageQuantity,
                                );
                                setQuantity(newQty);
                              }}
                            >
                              -
                            </span>
                            <input
                              type="text"
                              name="number"
                              value={quantity}
                              onChange={(e) => {
                                const value =
                                  parseFloat(e.target.value) || packageQuantity;
                                setQuantity(Math.max(packageQuantity, value));
                              }}
                              data-package-qty={packageQuantity}
                            />
                            <span
                              className="btn-quantity plus-btn"
                              onClick={() => {
                                setQuantity(quantity + packageQuantity);
                              }}
                            >
                              +
                            </span>
                          </div>
                        </div>
                        <a
                          href="#"
                          className="tf-btn btn-fill radius-3 justify-content-center fw-6 fs-14 flex-grow-1 animate-hover-btn btn-add-to-cart"
                          onClick={(e) => {
                            e.preventDefault();
                            if (cachedProduct) {
                              const qty = Math.max(packageQuantity, quantity);
                              addItem({
                                ...cachedProduct,
                                id: productId || cachedProduct.id,
                                qty,
                              });
                              open();
                            }
                          }}
                        >
                          <span>
                            {language === "ar"
                              ? "أضف إلى السلة"
                              : "Add to Cart"}
                          </span>
                        </a>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* /default */}

          {/* tabs - part of product content */}
          <section className="flat-spacing-17 pt_0">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <div className="widget-tabs style-has-border">
                    <ul className="widget-menu-tab">
                      <li className="item-title active">
                        <span className="inner">
                          {language === "ar" ? "الوصف" : "Description"}
                        </span>
                      </li>
                      <li className="item-title">
                        <span className="inner">
                          {language === "ar"
                            ? "معلومات إضافية"
                            : "Additional Information"}
                        </span>
                      </li>
                      {/* <li className="item-title">
                    <span className="inner">Review</span>
                  </li>
                  <li className="item-title">
                    <span className="inner">Shipping</span>
                  </li>
                  <li className="item-title">
                    <span className="inner">Return Policies</span>
                  </li> */}
                    </ul>

                    <div className="widget-content-tab">
                      <div className="widget-content-inner active">
                        <div>
                          {(cachedProduct?.title || cachedProduct?.name) && (
                            <div className="mb_20">
                              <h3 className="fs-16 fw-6">
                                {cachedProduct?.title || cachedProduct?.name}
                              </h3>
                            </div>
                          )}
                          {cachedProduct?.att_specifications && (
                            <div className="mb_20">
                              <h3 className="fs-16 fw-5">
                                {language === "ar"
                                  ? "المواصفات"
                                  : "Specifications"}
                              </h3>
                              <p className="text_black-2">
                                {cachedProduct.att_specifications}
                              </p>
                            </div>
                          )}
                          {cachedProduct?.description && (
                            <div className="mb_30">
                              <h3 className="fs-16 fw-5">
                                {language === "ar" ? "الوصف" : "Description"}
                              </h3>
                              <p className="text_black-2">
                                {cachedProduct.description}
                              </p>
                            </div>
                          )}
                          {/* Demo features/materials removed in favor of real product data */}
                        </div>
                      </div>

                      <div className="widget-content-inner">
                        <table className="tf-pr-attrs">
                          <tbody>
                            <tr className="tf-attr-pa-color">
                              <th className="tf-attr-label">
                                {language === "ar" ? "اللون" : "Color"}
                              </th>
                              <td className="tf-attr-value">
                                <p>{cachedProduct?.att_color || "-"}</p>
                              </td>
                            </tr>
                            <tr className="tf-attr-pa-brand">
                              <th className="tf-attr-label">
                                {language === "ar"
                                  ? "العلامة التجارية"
                                  : "Brand"}
                              </th>
                              <td className="tf-attr-value">
                                <p>
                                  {cachedProduct?.brand ||
                                    cachedProduct?.att_model_name ||
                                    "-"}
                                </p>
                              </td>
                            </tr>
                            <tr className="tf-attr-pa-size">
                              <th className="tf-attr-label">
                                {language === "ar" ? "المقاس" : "Size"}
                              </th>
                              <td className="tf-attr-value">
                                <p>{cachedProduct?.att_size || "-"}</p>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="widget-content-inner">
                        <div className="tab-reviews write-cancel-review-wrap">
                          <div className="tab-reviews-heading">
                            <div className="top">
                              <div className="text-center">
                                <h1 className="number fw-6">4.8</h1>
                                <div className="list-star">
                                  <i className="icon icon-star"></i>
                                  <i className="icon icon-star"></i>
                                  <i className="icon icon-star"></i>
                                  <i className="icon icon-star"></i>
                                  <i className="icon icon-star"></i>
                                </div>
                                <p>(168 Ratings)</p>
                              </div>

                              <div className="rating-score">
                                <div className="item">
                                  <div className="number-1 text-caption-1">
                                    5
                                  </div>
                                  <i className="icon icon-star"></i>
                                  <div className="line-bg">
                                    <div style={{ width: "94.67%" }}></div>
                                  </div>
                                  <div className="number-2 text-caption-1">
                                    59
                                  </div>
                                </div>
                                <div className="item">
                                  <div className="number-1 text-caption-1">
                                    4
                                  </div>
                                  <i className="icon icon-star"></i>
                                  <div className="line-bg">
                                    <div style={{ width: "60%" }}></div>
                                  </div>
                                  <div className="number-2 text-caption-1">
                                    46
                                  </div>
                                </div>
                                <div className="item">
                                  <div className="number-1 text-caption-1">
                                    3
                                  </div>
                                  <i className="icon icon-star"></i>
                                  <div className="line-bg">
                                    <div style={{ width: "0%" }}></div>
                                  </div>
                                  <div className="number-2 text-caption-1">
                                    0
                                  </div>
                                </div>
                                <div className="item">
                                  <div className="number-1 text-caption-1">
                                    2
                                  </div>
                                  <i className="icon icon-star"></i>
                                  <div className="line-bg">
                                    <div style={{ width: "0%" }}></div>
                                  </div>
                                  <div className="number-2 text-caption-1">
                                    0
                                  </div>
                                </div>
                                <div className="item">
                                  <div className="number-1 text-caption-1">
                                    1
                                  </div>
                                  <i className="icon icon-star"></i>
                                  <div className="line-bg">
                                    <div style={{ width: "0%" }}></div>
                                  </div>
                                  <div className="number-2 text-caption-1">
                                    0
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="tf-btn btn-outline-dark fw-6 btn-comment-review btn-cancel-review">
                                Cancel Review
                              </div>
                              <div className="tf-btn btn-outline-dark fw-6 btn-comment-review btn-write-review">
                                Write a review
                              </div>
                            </div>
                          </div>

                          <div className="reply-comment cancel-review-wrap">
                            <div className="d-flex mb_24 gap-20 align-items-center justify-content-between flex-wrap">
                              <h5 className="">03 Comments</h5>
                              <div className="d-flex align-items-center gap-12">
                                <div className="text-caption-1">Sort by:</div>
                                <div
                                  className="tf-dropdown-sort"
                                  data-bs-toggle="dropdown"
                                >
                                  <div className="btn-select">
                                    <span className="text-sort-value">
                                      Most Recent
                                    </span>
                                    <span className="icon icon-arrow-down"></span>
                                  </div>
                                  <div className="dropdown-menu">
                                    <div className="select-item active">
                                      <span className="text-value-item">
                                        Most Recent
                                      </span>
                                    </div>
                                    <div className="select-item">
                                      <span className="text-value-item">
                                        Oldest
                                      </span>
                                    </div>
                                    <div className="select-item">
                                      <span className="text-value-item">
                                        Most Popular
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="reply-comment-wrap">
                              <div className="reply-comment-item">
                                <div className="user">
                                  <div className="image">
                                    <img
                                      src="https://erp-elsalab.odoo.com/web/image/530564"
                                      alt=""
                                    />
                                  </div>
                                  <div>
                                    <h6>
                                      <a href="#" className="link">
                                        Superb quality apparel that exceeds
                                        expectations
                                      </a>
                                    </h6>
                                    <div className="day text_black-2">
                                      1 days ago
                                    </div>
                                  </div>
                                </div>
                                <p className="text_black-2">
                                  Great theme - we were looking for a theme with
                                  lots of built in features and flexibility and
                                  this was perfect. We expected to need to
                                  employ a developer to add a few finishing
                                  touches. But we actually managed to do
                                  everything ourselves. We did have one small
                                  query and the support given was swift and
                                  helpful.
                                </p>
                              </div>

                              <div className="reply-comment-item type-reply">
                                <div className="user">
                                  <div className="image">
                                    <img
                                      src="https://erp-elsalab.odoo.com/web/image/530564"
                                      alt=""
                                    />
                                  </div>
                                  <div>
                                    <h6>
                                      <a href="#" className="link">
                                        Reply from Modave
                                      </a>
                                    </h6>
                                    <div className="day text_black-2">
                                      1 days ago
                                    </div>
                                  </div>
                                </div>
                                <p className="text_black-2">
                                  We love to hear it! Part of what we love most
                                  about Modave is how much it empowers store
                                  owners like yourself to build a beautiful
                                  website without having to hire a developer :)
                                  Thank you for this fantastic review!
                                </p>
                              </div>

                              <div className="reply-comment-item">
                                <div className="user">
                                  <div className="image">
                                    <img
                                      src="https://erp-elsalab.odoo.com/web/image/530564"
                                      alt=""
                                    />
                                  </div>
                                  <div>
                                    <h6>
                                      <a href="#" className="link">
                                        Superb quality apparel that exceeds
                                        expectations
                                      </a>
                                    </h6>
                                    <div className="day text_black-2">
                                      1 days ago{" "}
                                    </div>
                                  </div>
                                </div>
                                <p className="text_black-2">
                                  Great theme - we were looking for a theme with
                                  lots of built in features and flexibility and
                                  this was perfect. We expected to need to
                                  employ a developer to add a few finishing
                                  touches. But we actually managed to do
                                  everything ourselves. We did have one small
                                  query and the support given was swift and
                                  helpful.
                                </p>
                              </div>
                            </div>
                          </div>

                          <form className="form-write-review write-review-wrap">
                            <div className="heading">
                              <h5>Write a review:</h5>
                              <div className="list-rating-check">
                                <input
                                  type="radio"
                                  id="star5"
                                  name="rate"
                                  value="5"
                                />
                                <label htmlFor="star5" title="text"></label>
                                <input
                                  type="radio"
                                  id="star4"
                                  name="rate"
                                  value="4"
                                />
                                <label htmlFor="star4" title="text"></label>
                                <input
                                  type="radio"
                                  id="star3"
                                  name="rate"
                                  value="3"
                                />
                                <label htmlFor="star3" title="text"></label>
                                <input
                                  type="radio"
                                  id="star2"
                                  name="rate"
                                  value="2"
                                />
                                <label htmlFor="star2" title="text"></label>
                                <input
                                  type="radio"
                                  id="star1"
                                  name="rate"
                                  value="1"
                                />
                                <label htmlFor="star1" title="text"></label>
                              </div>
                            </div>

                            <div className="form-content">
                              <fieldset className="box-field">
                                <label className="label">Review Title</label>
                                <input
                                  type="text"
                                  placeholder="Give your review a title"
                                  name="text"
                                  tabIndex={2}
                                  aria-required="true"
                                  required
                                />
                              </fieldset>
                              <fieldset className="box-field">
                                <label className="label">Review</label>
                                <textarea
                                  rows={4}
                                  placeholder="Write your comment here"
                                  tabIndex={2}
                                  aria-required="true"
                                  required
                                ></textarea>
                              </fieldset>
                              <div className="box-field group-2">
                                <fieldset>
                                  <input
                                    type="text"
                                    placeholder="You Name (Public)"
                                    name="text"
                                    tabIndex={2}
                                    aria-required="true"
                                    required
                                  />
                                </fieldset>
                                <fieldset>
                                  <input
                                    type="email"
                                    placeholder="Your email (private)"
                                    name="email"
                                    tabIndex={2}
                                    aria-required="true"
                                    required
                                  />
                                </fieldset>
                              </div>
                              <div className="box-check">
                                <input
                                  type="checkbox"
                                  name="availability"
                                  className="tf-check"
                                  id="check1"
                                />
                                <label
                                  className="text_black-2"
                                  htmlFor="check1"
                                >
                                  Save my name, email, and website in this
                                  browser for the next time I comment.
                                </label>
                              </div>
                            </div>

                            <div className="button-submit">
                              <button
                                className="tf-btn btn-fill animate-hover-btn"
                                type="submit"
                              >
                                Submit Reviews
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>

                      <div className="widget-content-inner">
                        <ul className="d-flex justify-content-center mb_18">
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M8.7 30.7h22.7c.3 0 .6-.2.7-.6l4-25.3c-.1-.4-.3-.7-.7-.8s-.7.2-.8.6L34 8.9l-3-1.1c-2.4-.9-5.1-.5-7.2 1-2.3 1.6-5.3 1.6-7.6 0-2.1-1.5-4.8-1.9-7.2-1L6 8.9l-.7-4.3c0-.4-.4-.7-.7-.6-.4.1-.6.4-.6.8l4 25.3c.1.3.3.6.7.6zm.8-21.6c2-.7 4.2-.4 6 .8 1.4 1 3 1.5 4.6 1.5s3.2-.5 4.6-1.5c1.7-1.2 4-1.6 6-.8l3.3 1.2-3 19.1H9.2l-3-19.1 3.3-1.2zM32 32H8c-.4 0-.7.3-.7.7s.3.7.7.7h24c.4 0 .7-.3.7-.7s-.3-.7-.7-.7zm0 2.7H8c-.4 0-.7.3-.7.7s.3.6.7.6h24c.4 0 .7-.3.7-.7s-.3-.6-.7-.6zm-17.9-8.9c-1 0-1.8-.3-2.4-.6l.1-2.1c.6.4 1.4.6 2 .6.8 0 1.2-.4 1.2-1.3s-.4-1.3-1.3-1.3h-1.3l.2-1.9h1.1c.6 0 1-.3 1-1.3 0-.8-.4-1.2-1.1-1.2s-1.2.2-1.9.4l-.2-1.9c.7-.4 1.5-.6 2.3-.6 2 0 3 1.3 3 2.9 0 1.2-.4 1.9-1.1 2.3 1 .4 1.3 1.4 1.3 2.5.3 1.8-.6 3.5-2.9 3.5zm4-5.5c0-3.9 1.2-5.5 3.2-5.5s3.2 1.6 3.2 5.5-1.2 5.5-3.2 5.5-3.2-1.6-3.2-5.5zm4.1 0c0-2-.1-3.5-.9-3.5s-1 1.5-1 3.5.1 3.5 1 3.5c.8 0 .9-1.5.9-3.5zm4.5-1.4c-.9 0-1.5-.8-1.5-2.1s.6-2.1 1.5-2.1 1.5.8 1.5 2.1-.5 2.1-1.5 2.1zm0-.8c.4 0 .7-.5.7-1.2s-.2-1.2-.7-1.2-.7.5-.7 1.2.3 1.2.7 1.2z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M36.7 31.1l-2.8-1.3-4.7-9.1 7.5-3.5c.4-.2.6-.6.4-1s-.6-.5-1-.4l-7.5 3.5-7.8-15c-.3-.5-1.1-.5-1.4 0l-7.8 15L4 15.9c-.4-.2-.8 0-1 .4s0 .8.4 1l7.5 3.5-4.7 9.1-2.8 1.3c-.4.2-.6.6-.4 1 .1.3.4.4.7.4.1 0 .2 0 .3-.1l1-.4-1.5 2.8c-.1.2-.1.5 0 .8.1.2.4.3.7.3h31.7c.3 0 .5-.1.7-.4.1-.2.1-.5 0-.8L35.1 32l1 .4c.1 0 .2.1.3.1.3 0 .6-.2.7-.4.1-.3 0-.8-.4-1zm-5.1-2.3l-9.8-4.6 6-2.8 3.8 7.4zM20 6.4L27.1 20 20 23.3 12.9 20 20 6.4zm-7.8 15l6 2.8-9.8 4.6 3.8-7.4zm22.4 13.1H5.4L7.2 31 20 25l12.8 6 1.8 3.5z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M5.9 5.9v28.2h28.2V5.9H5.9zM19.1 20l-8.3 8.3c-2-2.2-3.2-5.1-3.2-8.3s1.2-6.1 3.2-8.3l8.3 8.3zm-7.4-9.3c2.2-2 5.1-3.2 8.3-3.2s6.1 1.2 8.3 3.2L20 19.1l-8.3-8.4zM20 20.9l8.3 8.3c-2.2 2-5.1 3.2-8.3 3.2s-6.1-1.2-8.3-3.2l8.3-8.3zm.9-.9l8.3-8.3c2 2.2 3.2 5.1 3.2 8.3s-1.2 6.1-3.2 8.3L20.9 20zm8.4-10.2c-1.2-1.1-2.6-2-4.1-2.6h6.6l-2.5 2.6zm-18.6 0L8.2 7.2h6.6c-1.5.6-2.9 1.5-4.1 2.6zm-.9.9c-1.1 1.2-2 2.6-2.6 4.1V8.2l2.6 2.5zM7.2 25.2c.6 1.5 1.5 2.9 2.6 4.1l-2.6 2.6v-6.7zm3.5 5c1.2 1.1 2.6 2 4.1 2.6H8.2l2.5-2.6zm18.6 0l2.6 2.6h-6.6c1.4-.6 2.8-1.5 4-2.6zm.9-.9c1.1-1.2 2-2.6 2.6-4.1v6.6l-2.6-2.5zm2.6-14.5c-.6-1.5-1.5-2.9-2.6-4.1l2.6-2.6v6.7z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M35.1 33.6L33.2 6.2c0-.4-.3-.7-.7-.7H13.9c-.4 0-.7.3-.7.7s.3.7.7.7h18l.7 10.5H20.8c-8.8.2-15.9 7.5-15.9 16.4 0 .4.3.7.7.7h28.9c.2 0 .4-.1.5-.2s.2-.3.2-.5v-.2h-.1zm-28.8-.5C6.7 25.3 13 19 20.8 18.9h11.9l1 14.2H6.3zm11.2-6.8c0 1.2-1 2.1-2.1 2.1s-2.1-1-2.1-2.1 1-2.1 2.1-2.1 2.1 1 2.1 2.1zm6.3 0c0 1.2-1 2.1-2.1 2.1-1.2 0-2.1-1-2.1-2.1s1-2.1 2.1-2.1 2.1 1 2.1 2.1z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M20 33.8c7.6 0 13.8-6.2 13.8-13.8S27.6 6.2 20 6.2 6.2 12.4 6.2 20 12.4 33.8 20 33.8zm0-26.3c6.9 0 12.5 5.6 12.5 12.5S26.9 32.5 20 32.5 7.5 26.9 7.5 20 13.1 7.5 20 7.5zm-.4 15h.5c1.8 0 3-1.1 3-3.7 0-2.2-1.1-3.6-3.1-3.6h-2.6v10.6h2.2v-3.3zm0-5.2h.4c.6 0 .9.5.9 1.7 0 1.1-.3 1.7-.9 1.7h-.4v-3.4z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M30.2 29.3c2.2-2.5 3.6-5.7 3.6-9.3s-1.4-6.8-3.6-9.3l3.6-3.6c.3-.3.3-.7 0-.9-.3-.3-.7-.3-.9 0l-3.6 3.6c-2.5-2.2-5.7-3.6-9.3-3.6s-6.8 1.4-9.3 3.6L7.1 6.2c-.3-.3-.7-.3-.9 0-.3.3-.3.7 0 .9l3.6 3.6c-2.2 2.5-3.6 5.7-3.6 9.3s1.4 6.8 3.6 9.3l-3.6 3.6c-.3.3-.3.7 0 .9.1.1.3.2.5.2s.3-.1.5-.2l3.6-3.6c2.5 2.2 5.7 3.6 9.3 3.6s6.8-1.4 9.3-3.6l3.6 3.6c.1.1.3.2.5.2s.3-.1.5-.2c.3-.3.3-.7 0-.9l-3.8-3.6z"
                              ></path>
                            </svg>
                          </li>
                          <li>
                            <svg
                              viewBox="0 0 40 40"
                              width="35px"
                              height="35px"
                              style={{ margin: "5px" }}
                            >
                              <path
                                fill="currentColor"
                                d="M34.1 34.1H5.9V5.9h28.2v28.2zM7.2 32.8h25.6V7.2H7.2v25.6zm13.5-18.3a.68.68 0 0 0-.7-.7.68.68 0 0 0-.7.7v10.9a.68.68 0 0 0 .7.7.68.68 0 0 0 .7-.7V14.5z"
                              ></path>
                            </svg>
                          </li>
                        </ul>
                        <p className="text-center text-paragraph">
                          LT01: 70% wool, 15% polyester, 10% polyamide, 5%
                          acrylic 900 Grms/mt
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* /widget-tabs */}
                </div>
              </div>
            </div>
          </section>
          {/* /tabs */}

          {/* product - part of product content */}
          <section className="flat-spacing-1 pt_0" style={{ display: "none" }}>
            <div className="container">
              <div className="flat-title">
                <span className="title">People Also Bought</span>
              </div>
              <div className="hover-sw-nav hover-sw-2">
                <div
                  dir="ltr"
                  className="swiper tf-sw-product-sell wrap-sw-over"
                  data-preview="4"
                  data-tablet="3"
                  data-mobile="2"
                  data-space-lg="30"
                  data-space-md="15"
                  data-pagination="2"
                  data-pagination-md="3"
                  data-pagination-lg="3"
                >
                  <div className="swiper-wrapper">
                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="list-product-btn">
                            <a
                              href="#quick_add"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Quick Add</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            {/* Add to Compare removed */}
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                          <div className="size-list">
                            <span>S</span>
                            <span>M</span>
                            <span>L</span>
                            <span>XL</span>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title link">
                            Ribbed Tank Top
                          </a>
                          <span className="price">$16.95</span>
                          <ul className="list-color-product">
                            <li className="list-color-item color-swatch active">
                              <span className="tooltip">Orange</span>
                              <span className="swatch-value bg_orange-3"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Black</span>
                              <span className="swatch-value bg_dark"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">White</span>
                              <span className="swatch-value bg_white"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="list-product-btn">
                            <a
                              href="#quick_add"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Quick Add</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            <a
                              href="#compare"
                              data-bs-toggle="offcanvas"
                              aria-controls="offcanvasLeft"
                              className="box-icon bg_white compare btn-icon-action"
                            >
                              <span className="icon icon-compare"></span>
                              <span className="tooltip">Add to Compare</span>
                              <span className="icon icon-check"></span>
                            </a>
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                          <div className="size-list">
                            <span>M</span>
                            <span>L</span>
                            <span>XL</span>
                          </div>
                          <div className="on-sale-wrap">
                            <div className="on-sale-item">-33%</div>
                          </div>
                          <div className="countdown-box">
                            <div
                              className="js-countdown"
                              data-timer="1007500"
                              data-labels="d :,h :,m :,s"
                            ></div>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title link">
                            Ribbed modal T-shirt
                          </a>
                          <span className="price">From $18.95</span>
                          <ul className="list-color-product">
                            <li className="list-color-item color-swatch active">
                              <span className="tooltip">Brown</span>
                              <span className="swatch-value bg_brown"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Light Purple</span>
                              <span className="swatch-value bg_purple"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Light Green</span>
                              <span className="swatch-value bg_light-green"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="list-product-btn">
                            <a
                              href="#shoppingCart"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Add to cart</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            <a
                              href="#compare"
                              data-bs-toggle="offcanvas"
                              aria-controls="offcanvasLeft"
                              className="box-icon bg_white compare btn-icon-action"
                            >
                              <span className="icon icon-compare"></span>
                              <span className="tooltip">Add to Compare</span>
                              <span className="icon icon-check"></span>
                            </a>
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                          <div className="size-list">
                            <span>S</span>
                            <span>M</span>
                            <span>L</span>
                            <span>XL</span>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title link">
                            Oversized Printed T-shirt
                          </a>
                          <span className="price">$10.00</span>
                        </div>
                      </div>
                    </div>

                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="list-product-btn">
                            <a
                              href="#quick_add"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Quick Add</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            <a
                              href="#compare"
                              data-bs-toggle="offcanvas"
                              aria-controls="offcanvasLeft"
                              className="box-icon bg_white compare btn-icon-action"
                            >
                              <span className="icon icon-compare"></span>
                              <span className="tooltip">Add to Compare</span>
                              <span className="icon icon-check"></span>
                            </a>
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                          <div className="size-list">
                            <span>S</span>
                            <span>M</span>
                            <span>L</span>
                            <span>XL</span>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title">
                            Oversized Printed T-shirt
                          </a>
                          <span className="price">$16.95</span>
                          <ul className="list-color-product">
                            <li className="list-color-item color-swatch active">
                              <span className="tooltip">White</span>
                              <span className="swatch-value bg_white"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Pink</span>
                              <span className="swatch-value bg_purple"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Black</span>
                              <span className="swatch-value bg_dark"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="size-list">
                            <span>S</span>
                            <span>M</span>
                            <span>L</span>
                            <span>XL</span>
                          </div>
                          <div className="list-product-btn">
                            <a
                              href="#quick_add"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Quick Add</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            <a
                              href="#compare"
                              data-bs-toggle="offcanvas"
                              aria-controls="offcanvasLeft"
                              className="box-icon bg_white compare btn-icon-action"
                            >
                              <span className="icon icon-compare"></span>
                              <span className="tooltip">Add to Compare</span>
                              <span className="icon icon-check"></span>
                            </a>
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title link">
                            V-neck linen T-shirt
                          </a>
                          <span className="price">$114.95</span>
                          <ul className="list-color-product">
                            <li className="list-color-item color-swatch active">
                              <span className="tooltip">Brown</span>
                              <span className="swatch-value bg_brown"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">White</span>
                              <span className="swatch-value bg_white"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="swiper-slide" lazy="true">
                      <div className="card-product">
                        <div className="card-product-wrapper">
                          <a href="product-detail.html" className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                            <img
                              className="lazyload img-hover"
                              data-src="https://erp-elsalab.odoo.com/web/image/530564"
                              src="https://erp-elsalab.odoo.com/web/image/530564"
                              alt="image-product"
                            />
                          </a>
                          <div className="list-product-btn absolute-2">
                            <a
                              href="#quick_add"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quick-add tf-btn-loading"
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">Quick Add</span>
                            </a>
                            <a
                              href="javascript:void(0);"
                              className="box-icon bg_white wishlist btn-icon-action"
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">Add to Wishlist</span>
                              <span className="icon icon-delete"></span>
                            </a>
                            <a
                              href="#compare"
                              data-bs-toggle="offcanvas"
                              aria-controls="offcanvasLeft"
                              className="box-icon bg_white compare btn-icon-action"
                            >
                              <span className="icon icon-compare"></span>
                              <span className="tooltip">Add to Compare</span>
                              <span className="icon icon-check"></span>
                            </a>
                            <a
                              href="#quick_view"
                              data-bs-toggle="modal"
                              className="box-icon bg_white quickview tf-btn-loading"
                            >
                              <span className="icon icon-view"></span>
                              <span className="tooltip">Quick View</span>
                            </a>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href="product-detail.html" className="title link">
                            Loose Fit Sweatshirt
                          </a>
                          <span className="price">$10.00</span>
                          <ul className="list-color-product">
                            <li className="list-color-item color-swatch active">
                              <span className="tooltip">Light Green</span>
                              <span className="swatch-value bg_light-green"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Black</span>
                              <span className="swatch-value bg_dark"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Blue</span>
                              <span className="swatch-value bg_blue-2"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Dark Blue</span>
                              <span className="swatch-value bg_dark-blue"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">White</span>
                              <span className="swatch-value bg_white"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                            <li className="list-color-item color-swatch">
                              <span className="tooltip">Light Grey</span>
                              <span className="swatch-value bg_light-grey"></span>
                              <img
                                className="lazyload"
                                data-src="https://erp-elsalab.odoo.com/web/image/530564"
                                src="https://erp-elsalab.odoo.com/web/image/530564"
                                alt="image-product"
                              />
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    {/* /slides */}
                  </div>
                </div>

                <div className="nav-sw nav-next-slider nav-next-product box-icon w_46 round">
                  <span className="icon icon-arrow-left"></span>
                </div>
                <div className="nav-sw nav-prev-slider nav-prev-product box-icon w_46 round">
                  <span className="icon icon-arrow-right"></span>
                </div>
                <div className="sw-dots style-2 sw-pagination-product justify-content-center"></div>
              </div>
            </div>
          </section>
          {/* /product */}
        </>
      )}

      {/* You may also like (API alternatives) */}
      {loadingAlternatives ? (
        <section className="flat-spacing-1 pt_0">
          <div className="container">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">
                  {language === "ar" ? "جاري التحميل..." : "Loading..."}
                </span>
              </div>
            </div>
          </div>
        </section>
      ) : alternatives && alternatives.length > 0 ? (
        <section className="flat-spacing-4 pt_0">
          <br />
          <br />
          <div className="container">
            <div className="flat-title">
              <span className="title">
                {language === "ar"
                  ? "منتجات قد تعجبك"
                  : "Products You May Like"}
              </span>
            </div>
            <div className="hover-sw-nav hover-sw-2">
              <div
                dir="ltr"
                className="swiper tf-sw-recent wrap-sw-over you-may-like-swiper"
                data-preview="4"
                data-tablet="3"
                data-mobile="2"
                data-space-lg="30"
                data-space-md="30"
                data-space="15"
                data-pagination="1"
                data-pagination-md="1"
                data-pagination-lg="1"
              >
                <div className="swiper-wrapper">
                  {alternatives.map((p) => (
                    <div key={p.id} className="swiper-slide" lazy="true">
                      <div className="card-product grid">
                        <div className="card-product-wrapper">
                          <a href={getProductLink(p)} className="product-img">
                            <img
                              className="lazyload img-product"
                              data-src={p.image}
                              src={p.image}
                              alt={p.title}
                            />
                            <img
                              className="lazyload img-hover"
                              data-src={p.image}
                              src={p.image}
                              alt={p.title}
                            />
                          </a>
                          <div className="list-product-btn absolute-2">
                            <a
                              href="#"
                              className="box-icon bg_white quick-add tf-btn-loading"
                              onClick={(e) => {
                                e.preventDefault();
                                addItem({ ...p, qty: 1 });
                                open();
                              }}
                            >
                              <span className="icon icon-bag"></span>
                              <span className="tooltip">
                                {language === "ar"
                                  ? "أضف إلى السلة"
                                  : "Add to cart"}
                              </span>
                            </a>
                            <button
                              type="button"
                              className={`box-icon bg_white wishlist btn-icon-action ${
                                alternativesFavoriteState[p.id] !== undefined
                                  ? alternativesFavoriteState[p.id]
                                  : p.is_favorite || false
                                    ? "active"
                                    : ""
                              }`}
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const currentFavorite =
                                    alternativesFavoriteState[p.id] !==
                                    undefined
                                      ? alternativesFavoriteState[p.id]
                                      : p.is_favorite || false;
                                  const newFavoriteState = !currentFavorite;

                                  // Optimistically update local state
                                  setAlternativesFavoriteState((prev) => ({
                                    ...prev,
                                    [p.id]: newFavoriteState,
                                  }));

                                  await toggleFavoriteProduct(
                                    parseInt(p.id),
                                    newFavoriteState,
                                    null,
                                    p,
                                  );

                                  console.log(
                                    `Product ${p.id} ${
                                      newFavoriteState
                                        ? "added to"
                                        : "removed from"
                                    } wishlist`,
                                  );
                                } catch (error) {
                                  // Revert on error
                                  setAlternativesFavoriteState((prev) => ({
                                    ...prev,
                                    [p.id]: p.is_favorite || false,
                                  }));
                                  console.error(
                                    "Failed to toggle favorite:",
                                    error,
                                  );
                                  alert(
                                    error.message ||
                                      (language === "ar"
                                        ? "فشل تحديث قائمة الأمنيات"
                                        : "Failed to update wishlist"),
                                  );
                                }
                              }}
                            >
                              <span className="icon icon-heart"></span>
                              <span className="tooltip">
                                {(
                                  alternativesFavoriteState[p.id] !== undefined
                                    ? alternativesFavoriteState[p.id]
                                    : p.is_favorite || false
                                )
                                  ? language === "ar"
                                    ? "إزالة من قائمة الأمنيات"
                                    : "Remove from Wishlist"
                                  : language === "ar"
                                    ? "أضف إلى قائمة الأمنيات"
                                    : "Add to Wishlist"}
                              </span>
                              <span className="icon icon-delete"></span>
                            </button>
                          </div>
                        </div>
                        <div className="card-product-info">
                          <a href={getProductLink(p)} className="title link">
                            {p.title}
                          </a>
                          {p.price != null && (
                            <div className="price-area">
                              <span
                                className="price current-price"
                                style={{ color: "var(--primary)" }}
                              >
                                EGP {Number(p.price).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="nav-sw nav-next-slider nav-next-youmay box-icon w_46 round">
                <span className="icon icon-arrow-left"></span>
              </div>
              <div className="nav-sw nav-prev-slider nav-prev-youmay box-icon w_46 round">
                <span className="icon icon-arrow-right"></span>
              </div>
              <div className="sw-dots style-2 sw-pagination-youmay justify-content-center"></div>
            </div>
          </div>
        </section>
      ) : null}

      <Footer />
      <GoTo />
    </div>
  );
}

export default ProductDetailPage;
